import { inject, Injectable, OnDestroy, DOCUMENT } from "@angular/core";
import { IActivityOptions, IActivityServiceEvent } from "../../types/activityService";
import { Observable, Subject, SubscriptionLike, fromEvent, merge, interval, timer } from "rxjs";
import { throttleTime, takeUntil, repeat, filter, map, distinctUntilChanged, debounceTime } from "rxjs/operators";
import { StorageService } from "./storageService.service";

export enum ActivityEvents {
  UserIsActive = 0,
  UserIsIdle = 1,
  UserIsBack = 2,
  TimeoutWarning = 3,
  UserHasTimedOut = 4
}

@Injectable()
export class ActivityService implements OnDestroy {

  private readonly _storageKey = "cms_user_last_activity"
  private readonly _defaults: IActivityOptions = {
    timeToIdle: 20 * 60,
    timeToTimeout: 30,
    listenFor: ["keydown", "mousewheel", "mousedown", "touchstart", "touchmove", "scroll"]
  };

  private readonly _document = inject(DOCUMENT);
  private readonly _storage = inject(StorageService);
  private readonly _subscriptions = new Set<SubscriptionLike>();
  private readonly _timedOut = new Subject<null>();
  private readonly _eventEmitter = new Subject<IActivityServiceEvent>();

  private _isRunning = false;
  private _isIdle = false;
  private _options: IActivityOptions = this._defaults;
  private _listenFor: Observable<Event>;
  private _userIsActive: Observable<Event | number>;
  private _interruptions: Observable<Event | number | null>;
  private _idleTimer: Observable<number>;

  /**
   * Configures the service with the given parameters.
   * @param options New options to configure the service. You can just pass the needed keys.
   */
  public configure(options: Partial<IActivityOptions>): ActivityService {
    this._options = { ...this._defaults, ...options };
    return this;
  }

  /**
   * Starts watching user activity.
   */
  public start(): void {
    if (this._isRunning) this.stop();
    this._rebuildObservables(this._options.listenFor, this._options.timeToIdle);
    this._isRunning = true;
    this._subscriptions.add(
      this._userIsActive.pipe(debounceTime(100))
        .subscribe(() => this._eventEmitter.next({ eventType: ActivityEvents.UserIsActive }))
    );
    this._subscriptions.add(
      this._idleTimer.subscribe(() => {
        this._eventEmitter.next({ eventType: ActivityEvents.UserIsIdle });
        this._startTimeoutCountdown();
      })
    );
    this._subscriptions.add(
      this._listenFor.subscribe(() => this._storage.set(this._storageKey, Date.now()))
    );
  }

  /**
   * Builds the needed observables for the service.
   * This includes timers and event listeners.
   * @param events A space-separated list of events to listen.
   * @param timeToIdle Inactive time in seconds that the user needs to be considered idle.
   */
  private _rebuildObservables(events: string[], timeToIdle: number): void {
    this._listenFor = this._getObservableFromEvents(events);
    const storageObservable = this._storage.observe<number>(this._storageKey).pipe(distinctUntilChanged());
    this._userIsActive = merge(this._listenFor, storageObservable);
    this._interruptions = merge(this._userIsActive, this._timedOut);
    this._idleTimer = timer(timeToIdle * 1000).pipe(
      takeUntil(this._userIsActive),
      repeat()
    );
  }

  private _getObservableFromEvents(events: string[]): Observable<Event> {
    const eventsObservables = events.map(
      event => fromEvent(this._document.documentElement, event, { passive: true })
        .pipe(throttleTime(500, undefined, { leading: true, trailing: true }))
    );
    return merge(...eventsObservables);
  }

  public ngOnDestroy(): void {
    if (this._isRunning) this.stop();
    this._eventEmitter.complete();
    this._timedOut.complete();
  }

  /**
   * Stops the service from running.
   * Service can be restarted by calling the start() method.
   * @param timedOut Specifies if the service was stopped because of the user being timedout.
   */
  public stop(): void {
    this._isIdle = false;
    this._isRunning = false;
    this._timedOut.next(null);
    this._unsubscribeAll();
  }

  private _unsubscribeAll(): void {
    this._subscriptions.forEach(subscription => subscription.unsubscribe());
    this._subscriptions.clear();
    this._clearObservableReferences();
  }

  private _clearObservableReferences() {
    this._listenFor = null;
    this._userIsActive = null;
    this._interruptions = null;
    this._idleTimer = null;
  }

  /**
   * Specifies if the service is running.
   */
  public get isRunning(): boolean {
    return this._isRunning;
  }

  /**
   * Starts the timeout countdown.
   * If the user performs a valid action, the countdown stops.
   */
  private _startTimeoutCountdown(): void {
    this._isIdle = true;
    let countdown = this._options.timeToTimeout;
    const countdownSubscription = interval(1000)
      .pipe(takeUntil(this._interruptions))
      .subscribe({
        next: () => {
          countdown--;
          this._eventEmitter.next({ eventType: ActivityEvents.TimeoutWarning, value: countdown });
          if (countdown > 0) return;
          this.stop();
          this._eventEmitter.next({ eventType: ActivityEvents.UserHasTimedOut });
        },
        complete: () => {
          this._subscriptions.delete(countdownSubscription);
          if (!this._isIdle) return;
          this._isIdle = false;
          this._eventEmitter.next({ eventType: ActivityEvents.UserIsBack });
        },
      });
    this._subscriptions.add(countdownSubscription);
  }

  /**
   * Returns an observable for a particular idle service event.
   * Use with takeUntilDestroyed() for automatic cleanup in components.
   * @param eventType Event to listen to.
   * @example
   * ```typescript
   * // Events without values
   * this.activityService.on(ActivityEvents.UserIsIdle)
   *   .pipe(takeUntilDestroyed())
   *   .subscribe(() => console.log('User is idle'));
   *
   * // TimeoutWarning returns countdown seconds
   * this.activityService.on(ActivityEvents.TimeoutWarning)
   *   .pipe(takeUntilDestroyed())
   *   .subscribe(seconds => console.log(`Timing out in ${seconds}s`));
   * ```
   */
  public on(eventType: ActivityEvents.UserIsActive): Observable<void>;
  public on(eventType: ActivityEvents.UserIsIdle): Observable<void>;
  public on(eventType: ActivityEvents.UserIsBack): Observable<void>;
  public on(eventType: ActivityEvents.TimeoutWarning): Observable<number>;
  public on(eventType: ActivityEvents.UserHasTimedOut): Observable<void>;
  public on(eventType: ActivityEvents): Observable<number | void>;
  public on(eventType: ActivityEvents): Observable<number | void> {
    return this._eventEmitter.pipe(
      filter(event => event.eventType === eventType),
      map(event => event.value)
    );
  }
}
